

import { 
  FileText, 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Activity 
} from "lucide-react";
import { formatCurrency } from "@/lib/data";

interface QuickStatsCardsProps {
  stats: {
    totalEvents: number;
    totalRevenue: number;
    totalPaid: number;
    totalProfit: number;
    avgProfitPercent: number;
    statusCounts: Record<string, number>;
    collectionRate: number;
  };
}

function QuickStatCard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  color,
  trend,
}: { 
  title: string; 
  value: string | number; 
  subtitle?: string;
  icon: React.ElementType;
  color: 'blue' | 'emerald' | 'amber' | 'rose' | 'violet' | 'cyan';
  trend?: { value: number; label: string };
}) {
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-100',
  };

  return (
    <div className={`p-4 rounded-xl border ${colorStyles[color]} transition-all hover:shadow-md flex-shrink-0 w-[140px] sm:w-auto`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium opacity-80 uppercase tracking-wider truncate">{title}</p>
          <p className="text-xl font-bold mt-1 truncate">{value}</p>
          {subtitle && <p className="text-xs mt-1 opacity-70 truncate">{subtitle}</p>}
          {trend && (
            <p className={`text-xs mt-1 font-medium ${trend.value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {trend.value >= 0 ? '+' : ''}{trend.value.toFixed(0)}% {trend.label}
            </p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-white/60 flex-shrink-0 ml-2`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export function QuickStatsCards({ stats }: QuickStatsCardsProps) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:overflow-visible">
      <QuickStatCard
        title="Total Events"
        value={stats.totalEvents}
        subtitle={`${stats.statusCounts['confirmed'] || 0} confirmed`}
        icon={FileText}
        color="blue"
      />
      <QuickStatCard
        title="Total Revenue"
        value={formatCurrency(stats.totalRevenue)}
        subtitle={`${stats.collectionRate.toFixed(0)}% collected`}
        icon={DollarSign}
        color="emerald"
        trend={{ value: stats.collectionRate, label: 'collection rate' }}
      />
      <QuickStatCard
        title="Net Profit"
        value={formatCurrency(stats.totalProfit)}
        subtitle={`${stats.avgProfitPercent.toFixed(1)}% avg margin`}
        icon={stats.totalProfit >= 0 ? TrendingUp : TrendingDown}
        color={stats.totalProfit >= 0 ? 'emerald' : 'rose'}
      />
      <QuickStatCard
        title="Outstanding"
        value={formatCurrency(stats.totalRevenue - stats.totalPaid)}
        subtitle="awaiting payment"
        icon={Activity}
        color="amber"
      />
    </div>
  );
}
